import {
  DEFAULT_PRODUCT,
  PLATFORM_NAME,
  type Product,
  productName,
} from '@app/shared/brand'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { orgProductsOf, useSpaceIfAny } from '../lib/org'
import { appOrigin, currentSite, DEMO_PATH } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { Button, Notice } from '../ui'
import { Logo } from '../ui/Logo'
import './login.css'

/**
 * Signed in, and a member of nothing.
 *
 * Every scoped screen resolves its subject from the active space, so a person
 * with none is handed a null one and each screen renders as an empty version of
 * itself — a members table with nobody in it, a schedule with no times. That
 * reads as a congregation whose data has gone, which is the wrong thing to
 * believe and the wrong thing to blame.
 *
 * It is not a rare case. It is what every account looks like between signing in
 * and being added to a shul, and what one looks like the day its membership is
 * removed.
 *
 * It wears the sign-in screen's clothes because it is the same moment as that
 * one — arrived, not yet inside — and it is the one signed-in screen that must
 * not draw the shell: the shell is a frame around work in a space, and there is
 * no space to frame.
 *
 * What is missing is said in the product's own words — a congregation, an
 * organization, a library — since a charity's office is not a shul's. Where
 * the person works in another of our products, that product is offered: a
 * gabbai who opens Terumah's app is not missing anything, they are in the
 * wrong one.
 *
 * Where they work elsewhere, the first such product is the primary action.
 * Otherwise, where the missing space is a congregation, the demo is: it ends
 * this session and then opens the demo door, which only signs in somebody who
 * is signed out. Sign out is always there beneath them.
 *
 * Only an org says where somebody works. Every account holds a library of its
 * own, so counting it would send everybody to Studio.
 */
export function NoSpaceScreen({
  email,
  onSignOut,
}: {
  email?: string | null
  onSignOut: () => Promise<void>
}) {
  const site = currentSite()
  const product = site === 'platform' || site === 'org' ? undefined : site
  const name = product ? productName(product) : PLATFORM_NAME
  const words = MISSING[product ?? DEFAULT_PRODUCT]
  const elsewhere = [
    ...orgProductsOf(useSpaceIfAny()?.everywhere ?? []),
  ].filter((p) => p !== product)
  /* Somebody who works in another product goes there; the demo is for the
     one with nowhere to go. */
  const offerDemo = product === DEFAULT_PRODUCT && elsewhere.length === 0
  const trpc = useTRPC()
  const handoff = useMutation(trpc.auth.handoff.mutationOptions())
  /* The session goes with them: a cookie cannot span our domains, so the API
     mints a short-lived token for that origin, as the app switcher does. */
  const openProduct = (to: Product) => {
    const origin = appOrigin(to)
    handoff.mutate(
      { to: origin },
      {
        onSuccess: (r) => window.location.assign(r.url),
        onError: () => window.location.assign(origin),
      },
    )
  }
  const [signOutError, setSignOutError] = useState<string | null>(null)
  const [signingOut, setSigningOut] = useState(false)

  const endSession = async () => {
    setSignOutError(null)
    setSigningOut(true)
    try {
      await onSignOut()
      return true
    } catch (err) {
      setSigningOut(false)
      setSignOutError(
        err instanceof Error ? err.message : 'Could not sign out.',
      )
      return false
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <h1 className="login__brand">
          <Logo size={28} product={product ?? 'platform'} />
          <span>{name}</span>
        </h1>
        <Notice>
          {email ? (
            <>
              You are signed in as <strong>{email}</strong>, and you do not
              belong to {words.place} here yet.
            </>
          ) : (
            <>You do not belong to {words.place} here yet.</>
          )}
        </Notice>
        <p className="login__lede">
          {words.lede}
          {offerDemo
            ? ' You can look around a demo congregation meanwhile.'
            : null}
          {elsewhere.length > 0
            ? ` You work in ${elsewhere.map(productName).join(' and ')}.`
            : null}
        </p>
        {signOutError ? <Notice tone="error">{signOutError}</Notice> : null}
        {elsewhere.map((other, i) => (
          <Button
            key={other}
            type="button"
            variant={i === 0 ? undefined : 'secondary'}
            disabled={handoff.isPending}
            onClick={() => openProduct(other)}
          >
            Open {productName(other)}
          </Button>
        ))}
        {offerDemo ? (
          <Button
            type="button"
            disabled={signingOut}
            onClick={() => {
              void endSession().then((ended) => {
                if (ended) window.location.assign(DEMO_PATH)
              })
            }}
          >
            Sign out and look around the demo
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          disabled={signingOut}
          onClick={() => {
            void endSession()
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  )
}

/** What a person without a space here is missing, in each product's words. */
const MISSING: Record<Product, { place: string; lede: string }> = {
  [DEFAULT_PRODUCT]: {
    place: 'a congregation',
    lede: 'Somebody who runs the shul adds you, and this becomes its back office. If you were a member until recently, your access has been removed.',
  },
  terumah: {
    place: 'an organization',
    lede: 'Somebody who runs the organization adds you, and this becomes its back office. If you worked with it until recently, your access has been removed.',
  },
  studio: {
    place: 'a library',
    lede: 'A library is made for you the first time you open Studio. Seeing this means something did not finish.',
  },
}
